import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, Send, History, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNotifications() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const getToken = () => localStorage.getItem("admin_token") || "";

  const fetchLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const response = await axios.get(`${API}/admin/notification-logs`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error("Failed to fetch notification logs:", err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error("Judul dan pesan harus diisi");
      return;
    }
    
    setIsSending(true);
    try {
      const response = await axios.post(`${API}/admin/send-notification`, {
        title: title.trim(),
        message: message.trim(),
        target: "all"
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success(response.data.message);
      setTitle("");
      setMessage("");
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim notifikasi");
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <AdminLayout title="Kirim Notifikasi">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="space-y-6"
      >
        {/* Send Notification Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Bell className="w-5 h-5 text-sky-600" />
              Kirim Notifikasi Manual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendNotification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Judul Notifikasi
                </label>
                <Input
                  type="text"
                  placeholder="Contoh: Pengumuman Penting"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-11"
                  maxLength={100}
                  data-testid="notification-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Isi Pesan
                </label>
                <textarea
                  placeholder="Tulis pesan notifikasi di sini..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full min-h-[100px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none text-sm"
                  maxLength={500}
                  data-testid="notification-message-input"
                />
                <p className="text-xs text-slate-400 mt-1">{message.length}/500 karakter</p>
              </div>
              <Button
                type="submit"
                disabled={isSending || !title.trim() || !message.trim()}
                className="w-full h-11 gap-2 bg-sky-600 hover:bg-sky-700"
                data-testid="send-notification-btn"
              >
                {isSending ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Kirim Notifikasi
                  </>
                )}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Notifikasi akan dikirim ke semua pengguna yang sudah install PWA
            </p>
          </CardContent>
        </Card>

        {/* Notification History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Riwayat Notifikasi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada notifikasi yang dikirim</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-slate-800">{log.title}</h4>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                        {log.sent_count} perangkat
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{log.message}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Oleh: {log.sent_by}</span>
                      <span>{formatDate(log.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AdminLayout>
  );
}
